import { createSlice } from '@reduxjs/toolkit';

// Initial state of the driver order, containing arrays of order images and a signature.
const initialState = {
  orderImages: [], // Existing images array
  orderSignature: null, // Added a field to store a single signature
};

// Create a slice of the Redux store for driver orders.
const slice = createSlice({
  name: 'driverOrder', // Name of the slice for identification.
  initialState: initialState, // Setting the initial state defined above.
  reducers: {
    // Action to set order images by adding a new image to the state.
    setOrderImages: (state, { payload }) => {
      return {
        ...state,
        orderImages: [...state.orderImages, payload], // Add the new image payload to the orderImages array.
      };
    },

    // Action to update the progress of an existing order image.
    setOrderImagesProgress: (state, { payload }) => {
      return {
        ...state,
        orderImages: state.orderImages.map((image) => {
          // Check if the current image matches the ID and index in the payload.
          if (image.id === payload.id && image.index === payload.index) {
            return {
              ...image,
              progress: payload.progress, // Update the progress of the matching image.
            };
          }
          return image; // Return the image unmodified if it doesn't match.
        }),
      };
    },

    // Action to remove an order image from the state.
    removeOrderImage: (state, { payload }) => {
      return {
        ...state,
        orderImages: state.orderImages.filter(
          (image) =>
            !(image.id === payload.id && image.index === payload.index), // Filter out the matching image.
        ),
      };
    },

    // Action to reset the order images state to its initial value.
    resetOrderImages: () => {
      return initialState; // Return the initial state, clearing all order images and signature.
    },

    // ---------------- Signature-related actions ------------------

    // Action to set the order signature.
    setOrderSignature: (state, { payload }) => {
      return {
        ...state,
        orderSignature: payload, // Set the signature payload in the state.
      };
    },

    // Action to update the progress of the signature.
    setOrderSignatureProgress: (state, { payload }) => {
      if (state.orderSignature && state.orderSignature.id === payload.id) {
        return {
          ...state,
          orderSignature: {
            ...state.orderSignature,
            progress: payload.progress, // Update the progress of the signature.
          },
        };
      }
      return state; // Return unmodified state if signature doesn't match.
    },

    // Action to remove the order signature.
    removeOrderSignature: (state) => {
      return {
        ...state,
        orderSignature: null, // Clear the signature.
      };
    },
  },
});

// Exporting actions generated by the slice for use in components or middleware.
export const {
  setOrderImages,
  setOrderImagesProgress,
  removeOrderImage,
  resetOrderImages,
  setOrderSignature, // Exporting the new signature actions
  setOrderSignatureProgress,
  removeOrderSignature,
} = slice.actions;

// Exporting the reducer function to be used in the Redux store.
export default slice.reducer;
